/**
 * Loads the tenant font catalog (`STOREFRONT_FONTS`, api-contract) on demand. Each import is a
 * `@fontsource-variable/*` package's CSS, which the bundler splits into the page's own assets, so
 * a font is fetched from the page's origin and only by a page whose theme names it.
 */
import type { StorefrontFontId } from "@loot-os/api-contract";

/** One lazy import per catalog font. Keyed by the contract's ids, so a font added there fails here. */
export const FONT_LOADERS: Record<StorefrontFontId, () => Promise<unknown>> = {
  inter: () => import("@fontsource-variable/inter"),
  manrope: () => import("@fontsource-variable/manrope"),
  "dm-sans": () => import("@fontsource-variable/dm-sans"),
  "plus-jakarta-sans": () => import("@fontsource-variable/plus-jakarta-sans"),
  figtree: () => import("@fontsource-variable/figtree"),
  geist: () => import("@fontsource-variable/geist"),
  outfit: () => import("@fontsource-variable/outfit"),
  sora: () => import("@fontsource-variable/sora"),
  montserrat: () => import("@fontsource-variable/montserrat"),
  "space-grotesk": () => import("@fontsource-variable/space-grotesk"),
  "bricolage-grotesque": () => import("@fontsource-variable/bricolage-grotesque"),
  oswald: () => import("@fontsource-variable/oswald"),
  "playfair-display": () => import("@fontsource-variable/playfair-display"),
  fraunces: () => import("@fontsource-variable/fraunces"),
  lora: () => import("@fontsource-variable/lora"),
  "jetbrains-mono": () => import("@fontsource-variable/jetbrains-mono"),
};

const requested = new Map<StorefrontFontId, Promise<void>>();

/**
 * Loads each named font once per page. Never rejects: a font that fails leaves its stack's
 * fallback on screen, and the next call retries it.
 */
export function loadFonts(ids: readonly StorefrontFontId[]): Promise<void> {
  return Promise.all(ids.map(loadFont)).then(() => undefined);
}

function loadFont(id: StorefrontFontId): Promise<void> {
  const pending = requested.get(id);
  if (pending) return pending;
  const loader = Object.hasOwn(FONT_LOADERS, id) ? FONT_LOADERS[id] : null;
  if (!loader) return Promise.resolve();
  const load = loader().then(
    () => undefined,
    () => {
      requested.delete(id);
    },
  );
  requested.set(id, load);
  return load;
}
